<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          v-for="(filter, index) in filtersObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import NegativationFilter from './NegativationFilter';
import ChannelFilter from './ChannelFilter';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import FiltersModel from '@/mixins/filters-model';
import { Flags } from '@/plugins/rollout';
import { isEqual, cloneDeep, differenceWith } from 'lodash';
import { mapGetters } from 'vuex';
import {
  DATA_TYPE,
  FILTER_NAMES,
} from '@/store/modules/cards/constants';

export default {
  name: 'FilterComparativeModal',
  components: {
    StIcon,
    NegativationFilter,
    ChannelFilter,
    GlobalFilterSummary,
    NegativationFilter,
  },
  mixins: [
    FiltersModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      globalFilterByDataType: {},
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.comparative.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('filter', [
      'filterByDataType',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    filtersObj() {
      const namesItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.comparative.notClassified'),
          value: 'Sem Grupos',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.names?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      return [
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.GROUP,
          props: {
            label: this.$t('components.filter.ModalFilters.label.comparative.groupsLabel'),
            selectItems: namesItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.GROUP),
          },
        },
        {
          component: 'ChannelFilter',
          filterType: FILTER_NAMES.CHANNEL,
          props: {
            isBigFour: true,
            clearable: this.displayClearable(FILTER_NAMES.CHANNEL),
          },
        },
      ];
    },
    filterTitleLabel() {
      return `${this.filterLabel} - ${this.card.props.title}`;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter));
    },
    showClearFilter() {
      return Object.keys(this.appliedFilter).length;
    },
  },
  created() {
    this.globalFilterByDataType = this.filterByDataType[this.card.dataType];
    this.globalFilterByDataTypeToShow = this.globalFilterByDataType;

    if ('filter' in this.card.props) {
      this.appliedFilter = cloneDeep(this.card.props.filter);
      this.tempFilter = cloneDeep(this.card.props.filter);

      const keysToUse = differenceWith(Object.keys(this.globalFilterByDataType), Object.keys(this.tempFilter), isEqual);

      this.globalFilterByDataTypeToShow = Object.fromEntries(Object.entries(this.globalFilterByDataType).filter(global => keysToUse.includes(global[0])));
    }
  },
  methods: {
    closeDialog() {
      this.$emit('input', false);
    },
    clearFilter() {
      this.$emit('applyFilter', {});
      this.closeDialog();
    },
    applyFilter() {
      // Sanatize before send
      for (const filterProperty in this.tempFilter) {
        if (!this.tempFilter[filterProperty]) delete this.tempFilter[filterProperty];
      }

      this.$emit('applyFilter', this.tempFilter);
      this.closeDialog();
    },
    clearChangesFilter() {
      this.tempFilter = cloneDeep(this.appliedFilter);
    },
    generateObjectToFilter(value) {
      return {
        text: value,
        value,
      };
    },
    displayClearable(componentKey) {
      return Boolean(this.showGlobalFilterFeature && this.globalFilterByDataType?.hasOwnProperty(componentKey));
    },
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.COMPETITIVE });
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
